import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { FileText, Download, RefreshCw, Calendar, TrendingUp, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { IndianRupee } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenDialog, setShowGenDialog] = useState(false);
  const [genYear, setGenYear] = useState(String(new Date().getFullYear()));
  const [genMonth, setGenMonth] = useState(String(new Date().getMonth() || 12)); // prev month
  const [lastGenResult, setLastGenResult] = useState(null);

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/monthly/list');
      setReports(res.data);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/reports/monthly/generate?year=${genYear}&month=${genMonth}`);
      toast.success(res.data.message);
      setLastGenResult(res.data);
      fetchReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate report'); }
    finally { setGenerating(false); }
  };

  const downloadReport = async (year, month, monthName) => {
    try {
      const res = await api.get(`/reports/monthly/download?year=${year}&month=${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `FinTrack_Performance_${monthName.replace(' ', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch { toast.error('Download failed'); }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => String(currentYear - i));

  return (
    <div className="p-8 min-h-full bg-slate-50" data-testid="reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1" data-testid="reports-heading">Monthly Reports</h1>
          <p className="text-slate-500">Auto-generated on the 1st of every month · Hierarchy performance view</p>
        </div>
        <Button onClick={() => { setShowGenDialog(true); setLastGenResult(null); }}
          className="bg-[#5B6FD6] hover:bg-[#4A5BC4] flex items-center gap-2" data-testid="generate-report-btn">
          <FileText className="h-4 w-4" /> Generate Report
        </Button>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="text-center py-16 text-slate-400">Loading reports...</div>
      ) : reports.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-semibold text-slate-600">No reports yet</p>
            <p className="text-sm text-slate-400 mt-1">Click "Generate Report" to create your first monthly performance report</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {reports.map(r => (
            <Card key={`${r.year}-${r.month}`} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`report-${r.year}-${r.month}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-[#5B6FD6]/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-6 w-6 text-[#5B6FD6]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-base">{r.month_name}</h3>
                      <p className="text-xs text-slate-400">
                        Generated {new Date(r.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {r.generated_by === 'system' && <Badge variant="outline" className="ml-2 text-[10px]">Auto</Badge>}
                      </p>
                    </div>
                  </div>

                  {/* Quick stats */}
                  <div className="hidden md:flex items-center gap-6">
                    {r.company_stats && (
                      <>
                        <div className="text-center">
                          <p className="text-xs text-slate-400">Completed</p>
                          <p className="text-sm font-bold text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3.5 w-3.5" /> {r.company_stats.completed}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-400">Rate</p>
                          <p className="text-sm font-bold text-[#5B6FD6]">
                            <TrendingUp className="h-3.5 w-3.5 inline mr-0.5" />{r.company_stats.completion_rate}%
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-400">Hours</p>
                          <p className="text-sm font-bold text-amber-600 flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" /> {r.company_stats.total_hours}h
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-400">Billing</p>
                          <p className="text-sm font-bold text-slate-700 flex items-center gap-1">
                            <IndianRupee className="h-3.5 w-3.5" /> {r.company_stats.billing_total?.toLocaleString('en-IN')}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <Button variant="outline" size="sm" onClick={() => downloadReport(r.year, r.month, r.month_name)}
                    className="flex items-center gap-2 text-[#5B6FD6] border-[#5B6FD6]/30 hover:bg-[#5B6FD6]/5"
                    data-testid={`download-${r.year}-${r.month}`}>
                    <Download className="h-4 w-4" /> Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Generate Report Dialog */}
      <Dialog open={showGenDialog} onOpenChange={setShowGenDialog}>
        <DialogContent className="max-w-sm" data-testid="generate-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#5B6FD6]" /> Generate Monthly Report</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Select the month to generate a performance report PDF</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Year</label>
                <Select value={genYear} onValueChange={setGenYear}>
                  <SelectTrigger data-testid="gen-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Month</label>
                <Select value={genMonth} onValueChange={setGenMonth}>
                  <SelectTrigger data-testid="gen-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {lastGenResult && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-3 text-sm">
                  <p className="font-medium text-green-700 mb-1">{lastGenResult.message}</p>
                  <div className="grid grid-cols-2 gap-1 text-xs text-green-600">
                    <span>Completion: {lastGenResult.stats?.completion_rate}%</span>
                    <span>Hours: {lastGenResult.stats?.total_hours}h</span>
                    <span>Completed: {lastGenResult.stats?.completed} tasks</span>
                    <span>Billing: ₹{lastGenResult.stats?.billing_total?.toLocaleString('en-IN')}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowGenDialog(false)}>Cancel</Button>
              <Button className="flex-1 bg-[#5B6FD6] hover:bg-[#4A5BC4]" onClick={generateReport}
                disabled={generating} data-testid="generate-confirm-btn">
                {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Generating...</> : <><RefreshCw className="h-4 w-4 mr-1" /> Generate</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
